import { useEffect, useMemo, useState } from "react";
import type { PlatformClient } from "../../platform/supabase";
import "./WalletSelfDiscoveryBoard.css";

type AssessmentSummary={
  administrationId:string;
  instrumentId:string;
  instrumentName:string;
  instrumentPurpose?:string|null;
  title:string;
  versionLabel:string;
  completedAt?:string|null;
  resultCount:number;
  visibleInsightCount:number;
};

type Insight={
  resultId:string;
  administrationId:string;
  instrumentId:string;
  instrumentName:string;
  assessmentTitle:string;
  versionLabel:string;
  completedAt?:string|null;
  targetType:string;
  targetRef?:string|null;
  targetName:string;
  rawScore:number;
  normalizedScore?:number|null;
  percentile?:number|null;
  interpretation?:{
    label:string;
    summary:string;
    recommendation?:string|null;
  }|null;
};

type BoardData={
  learnerId:string;
  counts:{
    assigned:number;
    completed:number;
    visibleInsights:number;
  };
  assessments:AssessmentSummary[];
  insights:Insight[];
  trust:{
    interpretationSource:string;
    crossInstrumentRanking:boolean;
    careerInference:boolean;
    competencyInference:boolean;
  };
};

function formatDate(value?:string|null){
  if(!value)return "-";
  const d=new Date(value);
  if(Number.isNaN(d.getTime()))return value;
  return d.toLocaleDateString("ko-KR",{year:"numeric",month:"2-digit",day:"2-digit"});
}

function scoreText(item:Insight){
  if(item.normalizedScore!==null && item.normalizedScore!==undefined){
    return Number(item.normalizedScore).toFixed(Number(item.normalizedScore)%1===0?0:1);
  }
  const raw=Number(item.rawScore);
  return Number.isFinite(raw) ? raw.toFixed(raw%1===0?0:2) : String(item.rawScore);
}

export function WalletSelfDiscoveryBoard({
  client,
  onNavigate,
  onUseInPortfolio
}:{
  client:PlatformClient;
  onNavigate:(path:string)=>void;
  onUseInPortfolio:(resultId:string)=>void;
}){
  const [data,setData]=useState<BoardData|null>(null);
  const [error,setError]=useState("");
  const [filter,setFilter]=useState("ALL");

  useEffect(()=>{
    let active=true;
    client.rpc("wallet_self_discovery_board").then(({data,error})=>{
      if(!active)return;
      if(error){setError(error.message);return;}
      setData(data as BoardData);
    });
    return()=>{active=false;};
  },[client]);

  const visible=useMemo(()=>{
    if(!data)return [];
    if(filter==="ALL")return data.insights;
    return data.insights.filter((item)=>item.administrationId===filter);
  },[data,filter]);

  if(error)return <p className="error">{error}</p>;
  if(!data)return <p className="muted">자기이해 보드를 준비하는 중…</p>;

  return (
    <section className="self-discovery-board">
      <div className="self-discovery-hero">
        <div>
          <p className="eyebrow">SELF-DISCOVERY</p>
          <h2>검사 결과를 한곳에서 다시 봐요</h2>
          <p>
            서로 다른 검사의 점수를 순위로 비교하지 않고, 각 검사에 이미 발행된 해석을
            출처와 함께 모아봅니다.
          </p>
        </div>
        <div className="self-discovery-summary">
          <div><span>배정 검사</span><strong>{data.counts.assigned}</strong></div>
          <div><span>완료 검사</span><strong>{data.counts.completed}</strong></div>
          <div><span>공개 해석</span><strong>{data.counts.visibleInsights}</strong></div>
        </div>
      </div>

      {data.assessments.length===0 ? (
        <div className="self-discovery-empty">
          <strong>아직 완료된 검사가 없습니다.</strong>
          <p>검사를 완료하면 공개 가능한 해석만 이곳에 모여 자기이해 기록으로 쌓입니다.</p>
        </div>
      ) : (
        <>
          <div className="self-discovery-filter">
            <button className={filter==="ALL"?"active":""} onClick={()=>setFilter("ALL")}>전체</button>
            {data.assessments.map((item)=>(
              <button
                key={item.administrationId}
                className={filter===item.administrationId?"active":""}
                onClick={()=>setFilter(item.administrationId)}
              >
                {item.title}
              </button>
            ))}
          </div>

          <div className="self-discovery-assessment-list">
            {data.assessments.map((item)=>(
              <article key={item.administrationId}>
                <div>
                  <span>{item.instrumentName} · v{item.versionLabel}</span>
                  <strong>{item.title}</strong>
                  {item.instrumentPurpose && <p>{item.instrumentPurpose}</p>}
                </div>
                <div>
                  <small>{formatDate(item.completedAt)}</small>
                  <em>{item.visibleInsightCount}/{item.resultCount} 공개 해석</em>
                </div>
              </article>
            ))}
          </div>

          <div className="self-discovery-insight-grid">
            {visible.map((item)=>(
              <article className="self-discovery-insight-card" key={item.resultId}>
                <header>
                  <div>
                    <span>{item.instrumentName}</span>
                    <strong>{item.targetName}</strong>
                  </div>
                  <div className="self-discovery-score">
                    <strong>{scoreText(item)}</strong>
                    <small>{item.normalizedScore!=null?"정규화 점수":"원점수"}</small>
                    {item.percentile!=null && <small>백분위 {item.percentile}</small>}
                  </div>
                </header>

                {item.interpretation ? (
                  <div className="self-discovery-interpretation">
                    <h3>{item.interpretation.label}</h3>
                    <p>{item.interpretation.summary}</p>
                    {item.interpretation.recommendation && (
                      <div className="self-discovery-question">
                        <strong>살펴볼 제안</strong>
                        <span>{item.interpretation.recommendation}</span>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="self-discovery-no-interpretation">
                    이 결과에는 학생에게 공개 가능한 해석 규칙이 없습니다.
                  </div>
                )}

                <footer>
                  <span>{item.assessmentTitle} · {formatDate(item.completedAt)}</span>
                  <button onClick={()=>onUseInPortfolio(item.resultId)}>문서에 활용</button>
                </footer>
              </article>
            ))}
          </div>

          {!visible.length && (
            <div className="self-discovery-empty">
              <strong>표시할 결과가 없습니다.</strong>
              <p>선택한 검사에는 아직 공개 가능한 결과가 없습니다.</p>
            </div>
          )}
        </>
      )}

      <div className="self-discovery-next-step">
        <div>
          <strong>결과를 답으로 고정하지 마세요</strong>
          <span>검사에서 발견한 단서를 실제 경험과 목표 속에서 다시 확인해보세요.</span>
        </div>
        <button className="secondary" onClick={()=>onNavigate("/goals")}>목표·다음 행동으로 이어가기</button>
      </div>

      <div className="self-discovery-trust">
        <strong>해석 기준</strong>
        <span>
          이 화면은 발행된 검사 결과와 학생 공개용 해석 규칙만 사용합니다.
          서로 다른 검사 간 점수 우열, 직업·전공 확정, 역량 자동판정은 하지 않습니다.
        </span>
      </div>
    </section>
  );
}
